import { MARKER_HEIGHT, MARKER_WIDTH } from '@repo/types/maps/map-marker';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import type * as MapLibre from 'maplibre-gl';

export interface ScreenPoint {
    readonly x: number;
    readonly y: number;
}

interface IndexedPin {
    readonly feature: MapDataLocationFeature;
    readonly x: number;
    readonly y: number;
}

interface ProjectableMap {
    project(coordinates: [number, number]): ScreenPoint;
}

interface ViewportProjectableMap extends ProjectableMap {
    getContainer(): {
        clientWidth: number;
        clientHeight: number;
    };
}

export interface PinScreenIndex {
    readonly cellSize: number;
    readonly cells: Map<string, IndexedPin[]>;
}

export const PIN_HIT_RADIUS_PX = Math.ceil(Math.max(MARKER_WIDTH, MARKER_HEIGHT) / 2);

const getCellKey = (x: number, y: number) => `${x},${y}`;

export function getPinHitTestTransformKey(map: MapLibre.Map): string {
    const container = map.getContainer();
    return [map.getZoom(), map.getBearing(), map.getPitch(), container.clientWidth, container.clientHeight].join('|');
}

export function createPinScreenIndex(
    map: ProjectableMap,
    pins: readonly MapDataLocationFeature[],
    cellSize = PIN_HIT_RADIUS_PX,
): PinScreenIndex {
    const cells = new Map<string, IndexedPin[]>();

    for (const feature of pins) {
        if (feature.geometry.type !== 'Point') continue;

        const point = map.project(feature.geometry.coordinates);
        if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;

        const cellX = Math.floor(point.x / cellSize);
        const cellY = Math.floor(point.y / cellSize);
        const key = getCellKey(cellX, cellY);
        const bucket = cells.get(key);
        const indexedPin = { feature, x: point.x, y: point.y };

        if (bucket) {
            bucket.push(indexedPin);
        } else {
            cells.set(key, [indexedPin]);
        }
    }

    return { cellSize, cells };
}

export function getPinsInPaddedViewport(
    map: ViewportProjectableMap,
    pins: readonly MapDataLocationFeature[],
    paddingPx: number,
): MapDataLocationFeature[] {
    const container = map.getContainer();
    const width = container.clientWidth;
    const height = container.clientHeight;

    if (width <= 0 || height <= 0) {
        return [...pins];
    }

    const minX = -paddingPx;
    const maxX = width + paddingPx;
    const minY = -paddingPx;
    const maxY = height + paddingPx;

    return pins.filter(feature => {
        if (feature.geometry.type !== 'Point') return false;

        const point = map.project(feature.geometry.coordinates);
        return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
    });
}

export function translateScreenPointToCachedTransform(
    point: ScreenPoint,
    currentAnchorPoint: ScreenPoint,
    cachedAnchorPoint: ScreenPoint,
): ScreenPoint {
    return {
        x: point.x - (currentAnchorPoint.x - cachedAnchorPoint.x),
        y: point.y - (currentAnchorPoint.y - cachedAnchorPoint.y),
    };
}

export function findClosestPinInScreenIndex(
    index: PinScreenIndex,
    point: ScreenPoint,
    maxDistancePx = PIN_HIT_RADIUS_PX,
): MapDataLocationFeature | null {
    const originCellX = Math.floor(point.x / index.cellSize);
    const originCellY = Math.floor(point.y / index.cellSize);
    const cellRadius = Math.ceil(maxDistancePx / index.cellSize);
    const maxDistanceSq = maxDistancePx * maxDistancePx;
    let closestDistanceSq = maxDistanceSq;
    let closestPin: MapDataLocationFeature | null = null;

    for (let dy = -cellRadius; dy <= cellRadius; dy++) {
        for (let dx = -cellRadius; dx <= cellRadius; dx++) {
            const bucket = index.cells.get(getCellKey(originCellX + dx, originCellY + dy));
            if (!bucket) continue;

            for (const pin of bucket) {
                const distanceX = pin.x - point.x;
                const distanceY = pin.y - point.y;
                const distanceSq = distanceX * distanceX + distanceY * distanceY;

                if (distanceSq < closestDistanceSq) {
                    closestDistanceSq = distanceSq;
                    closestPin = pin.feature;
                }
            }
        }
    }

    return closestPin;
}
